import {
  Controller,
  ControllerRenderProps,
  useFormContext,
} from "react-hook-form";
import { useEffect, useState } from "react";
import Upload, { UploadProps } from "rc-upload";
import { getAccessToken } from "../../../helpers/tokenManager";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import Button from "../../button/Button";
import UploadFileItem, { FileItem } from "./UploadFileItem";
import { RcFile } from "rc-upload/lib/interface";
import { CustomerPortalRequestMessageAttachment } from "../../../api/models/unified";

interface UploadInputProps {
  name: string;
  uploadPath: string;
  multiple?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}

const UploadInput = ({
  name,
  uploadPath,
  multiple,
  onUploadingChange,
}: UploadInputProps) => {
  const { tInput } = useCustomTranslation();

  const { control } = useFormContext();

  const [fileList, setFileList] = useState<FileItem[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0); // internal counter

  const isUploading = uploadingCount > 0;

  // Notify parent AFTER render, not during render. If we do not doing it inside effect, it throwis lot of warnings
  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  const updateFile = (uid: string, update: Partial<FileItem>) => {
    setFileList((prev) =>
      prev.map((file) => (file.uid === uid ? { ...file, ...update } : file)),
    );
  };

  const bumpUploading = (delta: number) => {
    setUploadingCount((prev) => Math.max(0, prev + delta));
  };

  const removeFile = (
    file: FileItem,
    field: ControllerRenderProps<Record<string, string[]>, string>,
  ) => {
    field.onChange(field.value?.filter((val) => val !== file.attachmentId));
    setFileList((prev) => prev.filter((x) => x.uid !== file.uid));
  };

  const handleUploadSuccess = (
    response: Record<string, unknown>,
    file: RcFile,
    field: ControllerRenderProps<Record<string, string[]>, string>,
  ) => {
    const typedResponse =
      response as unknown as CustomerPortalRequestMessageAttachment;

    updateFile(file.uid, {
      status: "success",
      percent: 100,
      attachmentId: typedResponse.id,
    });
    bumpUploading(-1);
    field.onChange([...(field.value ?? []), typedResponse.id]);
  };

  const uploadProps: UploadProps = {
    action: uploadPath,
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
    multiple,
    onStart: (file) => {
      bumpUploading(+1);
      setFileList((prev) => [
        ...prev,
        { uid: file.uid, name: file.name, status: "uploading", percent: 0 },
      ]);
    },
    onProgress: (event, file) => {
      updateFile(file.uid, { percent: event.percent });
    },
    onError: (_error, ret, file) => {
      updateFile(file.uid, { status: "fail" });
      bumpUploading(-1);
    },
  };

  return (
    <Controller<Record<string, string[]>>
      name={name}
      control={control}
      render={({ field }) => (
        <div className="flex flex-1 flex-col">
          <div className="mt-2 flex flex-1 flex-col">
            {fileList.map((file) => (
              <UploadFileItem
                key={file.uid}
                item={file}
                onRemove={() => removeFile(file, field)}
              />
            ))}
          </div>
          <Upload
            {...uploadProps}
            {...field}
            id={name}
            className="w-fit"
            name="file"
            onSuccess={(response, file) =>
              handleUploadSuccess(response, file, field)
            }
          >
            <Button
              size="small"
              icon="paperclip"
              label={tInput("upload_attachment")}
            />
          </Upload>
        </div>
      )}
    />
  );
};

export default UploadInput;
